import { useQuery } from '@apollo/client';
import { ColumnDef } from '@tanstack/react-table';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import PriceFormat from '@website/components/utilities/PriceFormat';
import {
  AdminFetchRevenueReportDocument,
  AdminFetchRevenueReportQuery,
  RevenueReportType,
} from '@website/types/graphqlOperations';
import { Button, Col, Row } from 'react-bootstrap';
import DataTable from '../DataTable';
import DownloadRevenueReportCSVButton from './DownloadRevenueReportCSVButton';

const revenueReportTable: ColumnDef<
  AdminFetchRevenueReportQuery['revenueReport']['items'][number] & {
    totalCredits?: AdminFetchRevenueReportQuery['totalCredits'];
  }
>[] = [
  {
    header: 'Period',
    accessorFn: (row) => row.period,
    cell: ({ row, getValue }) => {
      if (row.original.type === RevenueReportType.QUARTERLY) {
        return getValue<string>().replaceAll('-', ' ');
      }
      return getValue<string>();
    },
  },
  {
    header: 'GMV',
    cell: ({ row }) => {
      const gmv = row.original.GMV;
      return (
        <PriceFormat
          amount={gmv.amountZD}
          currencyIso={gmv.currency}
          roundAmountOff
          hideCurrency
        />
      );
    },
  },
  {
    header: 'Net Commission',
    cell: ({ row }) => {
      const { netCommission } = row.original;
      return (
        <PriceFormat
          amount={netCommission.amountZD}
          currencyIso={netCommission.currency}
          roundAmountOff
          hideCurrency
        />
      );
    },
  },
  {
    header: 'Cash Received',
    cell: ({ row }) => {
      const { cashReceived } = row.original;
      return (
        <PriceFormat
          amount={cashReceived.amountZD}
          currencyIso={cashReceived.currency}
          roundAmountOff
          hideCurrency
        />
      );
    },
  },
  {
    header: 'No. of Bookings',
    accessorFn: (row) => row.numberOfBookingMade,
  },
  {
    header: 'No. of Spaces',
    accessorFn: (row) => row.numberOfSpacesBooked,
  },
  {
    header: 'Avg Booking Value',
    cell: ({ row }) => {
      const { averageBookingValue } = row.original;
      return (
        <PriceFormat
          amount={averageBookingValue.amountZD}
          currencyIso={averageBookingValue.currency}
          roundAmountOff
          hideCurrency
        />
      );
    },
  },
  {
    header: 'Avg Commission/Booking',
    cell: ({ row }) => {
      const { averageNetCommission } = row.original;
      return (
        <PriceFormat
          amount={averageNetCommission.amountZD}
          currencyIso={averageNetCommission.currency}
          roundAmountOff
          hideCurrency
        />
      );
    },
  },
  {
    header: 'Avg People/Booking',
    accessorFn: (row) => row.averagePeoplePerBooking,
    cell: ({ row }) => {
      return row.original.averagePeoplePerBooking.toFixed(1);
    },
  },
  {
    header: 'Total Credit Amount',
    id: 'totalCredits',
    cell: ({ row }) => {
      const { totalCredits } = row.original;
      return (
        <PriceFormat
          amount={totalCredits.amountZD}
          currencyIso={totalCredits.currency}
          roundAmountOff
          hideCurrency
        />
      );
    },
  },
];

type RevenueReportProps = {
  type: RevenueReportType;
};

export default function RevenueReport({ type }: RevenueReportProps) {
  const { data, loading, error, fetchMore } = useQuery(
    AdminFetchRevenueReportDocument,
    {
      // poll every 2 minutes
      pollInterval: 120000,
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      variables: {
        type,
        // Just a hundred for now
        limit: 100,
      },
    },
  );

  if (error && !loading) {
    return <ErrorPage error={error} />;
  }

  if (loading || !data) {
    return <LoadingPage />;
  }

  return (
    <Row>
      <Col xs={12} className="py-3 d-flex justify-content-end">
        <DownloadRevenueReportCSVButton type={type} />
      </Col>

      <Col xs={12}>
        <DataTable
          data={data.revenueReport.items.map((item) => ({
            ...item,
            totalCredits: data.totalCredits,
          }))}
          columns={revenueReportTable}
          hiddenColumns={
            type === RevenueReportType.OVERALL ? [] : ['totalCredits']
          }
        />
      </Col>
      {data && data?.revenueReport?.nextToken && (
        <Col xs={12} className="text-center py-2">
          <Button
            disabled={loading}
            onClick={() => {
              fetchMore({
                variables: {
                  nextToken: data.revenueReport.nextToken,
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev;
                  return {
                    revenueReport: {
                      ...fetchMoreResult.revenueReport,
                      items: [
                        ...prev.revenueReport.items,
                        ...fetchMoreResult.revenueReport.items,
                      ],
                    },
                    totalCredits: fetchMoreResult.totalCredits,
                  };
                },
              });
            }}
          >
            {loading ? 'Loading...' : 'Load More Data'}
          </Button>
        </Col>
      )}
    </Row>
  );
}
